import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useHousehold } from '@/context/HouseholdContext'
import type { NetWorthItem, NetWorthKind } from '@/types/db'

export function useNetWorthItems() {
  const { householdId } = useHousehold()
  return useQuery({
    queryKey: ['netWorthItems', householdId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('net_worth_items')
        .select('*')
        .eq('household_id', householdId)
        .eq('archived', false)
        .order('kind')
        .order('name')
      if (error) throw error
      return data as NetWorthItem[]
    },
  })
}

export type NetWorthItemInput = Pick<NetWorthItem, 'name' | 'kind' | 'value' | 'currency'> & {
  item_type: string
  group_name?: string | null
  notes?: string | null
}

export function useSaveNetWorthItem() {
  const { householdId } = useHousehold()
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: NetWorthItemInput }) => {
      if (id) {
        const { error } = await supabase
          .from('net_worth_items')
          .update({ ...values, updated_at: new Date().toISOString() })
          .eq('id', id)
        if (error) throw error
      } else {
        const { error } = await supabase.from('net_worth_items').insert({ ...values, household_id: householdId })
        if (error) throw error
      }
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['netWorthItems'] }),
  })
}

export function useDeleteNetWorthItem() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('net_worth_items').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['netWorthItems'] }),
  })
}

export const NET_WORTH_KIND_LABEL: Record<NetWorthKind, string> = {
  asset: 'Bem / Ativo',
  liability: 'Dívida / Passivo',
}
